import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { FinancialConfig, WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderversePricePerformance_old {
  private container: HTMLElement;
  private config: FinancialConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'black',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    await this.injectChartLibrary();
    this.createWidget();
    this.attachStyles();
    await this.setupChart(this.config.symbol!);
    this.emit('ready', {});
    this.setupEventListeners();
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    const typeOptions = [
      "6 Month",
      "1 Year",
    ].map(s => `<option value="${s}">${s}</option>`).join('');

    const symbolOptions = this.fetchSymbols().map(s => `<option value="${s.value}">${s.label}</option>`).join('');
    const frequencyOptions = ['1D', '1W', '1M']
      .map(f => `<option value="${f}">${f}</option>`).join('');

    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${this.config.theme === 'white' ? 'whiteMode' : ''} ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Price Performance</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              <div class="item">
                <select class="symbol-dropdown">
                  ${symbolOptions}
                </select>
              </div>
              <span></span>
              <div class="item">
                <select class="type-dropdown">
            ${typeOptions}
                </select>
              </div>
              <div class="item">
                <select class="frequency-dropdown">${frequencyOptions}</select>
              </div>
            </div>

            <div class="chartDv" id="pricePerformanceContainer"></div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (symbolDropdown && this.config.symbol) {
      symbolDropdown.value = this.config.symbol;
    }

    const typeDropdown = this.shadowRoot.querySelector('.type-dropdown') as HTMLSelectElement;
    typeDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.type = value;
      this.setupChart(this.config.symbol!);
    });
    const frequencyDropdown = this.shadowRoot.querySelector('.frequency-dropdown') as HTMLSelectElement;
    frequencyDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value as '1D' | '1W' | '1M';
      (this.config as any).frequency = value;
      this.setupChart(this.config.symbol!);
    });
    if (frequencyDropdown) {
      frequencyDropdown.value = (this.config as any).frequency || '1D';
    }
  }


  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    const container = this.shadowRoot.querySelector('#pp-chart') as HTMLCanvasElement;
    if (container) {
      container.innerHTML = '';
    }
    const apiUrl = `${APIURL}/v3/ticker/stock_price_point_v2`;
    try {
      let interval = '1M';
      switch (this.config.type) {
        case "6 Month":
          interval = "6M";
          break;
        case "1 Year":
          interval = "1Y";
          break;
        default:
          interval = "1M";
          break;
      }

      // Use frequency from frequency dropdown (or default to 1D)
      const frequency = (this.config as any).frequency || '1D';

      const response = await apiGet({
        method: "POST",
        url: apiUrl,
        body: { symbol, frequency, interval },
      });
      const data = await response.json();
      this.currentData = data.data;
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (error) {
      console.log("Error fetching chart data:", error);
    }
  }


  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#pricePerformanceContainer') as HTMLElement;
    if (!container) return;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData)) {
      container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderAnalystRating();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector('#pricePerformanceContainer');
    if (!container) return;

    const getStyleWidth = (value: number) => {

    }

    const html = `
      <div class="pricePerformanceMain">
        <div id="pricePerformanceChart" class="chart"></div>
    </div>
    
    `;
    container.innerHTML = html;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#pricePerformanceChart') as HTMLElement;
    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    // Prepare data from this.currentData
    // Use raw ISO/timestamp values to let ECharts handle label density responsively
    const categories = this.currentData.map((item: any) => item.time_start);

    const candles = this.currentData.map((item: any) => [
      item.first_open,
      item.last_close,
      item.min_low,
      item.max_high
    ]);

    const volumes = this.currentData.map((item: any) => item.agg_volume);

    // Colors
    const upColor = '#00FF57';   // green
    const downColor = '#FF0000'; // red

    const option = {
      backgroundColor: 'transparent',
      animation: true,
      grid: [
        { left: 8, right: 46, top: 8, height: '68%', containLabel: false },
        { left: 8, right: 46, top: '75%', height: '15%', containLabel: false }
      ],
      xAxis: [
        {
          type: 'category',
          data: categories,
          boundaryGap: true,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: { color: '#2a2a2a', width: 1, type: 'dashed' }
          },
          axisLabel: { show: false }
        },
        {
          type: 'category',
          gridIndex: 1,
          data: categories,
          boundaryGap: true,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#9aa0a6',
            hideOverlap: true,
            interval: 'auto',
            formatter: function (value: any): string {
              const f = echarts.format || (window as any).echarts.format;
              return f.formatTime('MM-dd', value);
            }
          }
        }
      ],
      yAxis: [
        {
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: { color: '#2a2a2a', width: 1, type: 'dashed' }
          },
          splitNumber: 5,
          axisLabel: {
            color: '#9aa0a6'
          }
        },
        {
          gridIndex: 1,
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false }
        }
      ],
      series: [
        {
          type: 'candlestick',
          data: candles,
          itemStyle: {
            color: upColor,
            color0: downColor,
            borderColor: upColor,
            borderColor0: downColor
          },
          barMaxWidth: 6,
          emphasis: { scale: false },
          tooltip: { show: true }
        },
        {
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: volumes.map((v: any, i: number) => ({ value: v, idx: i })),
          barMaxWidth: 6,
          itemStyle: {
            color: (params: any) => {
              const i = params.data.idx;
              const open = candles[i][0];
              const close = candles[i][1];
              return close >= open ? upColor : downColor;
            }
          },
          animation: true,
          tooltip: { show: false }
        }
      ]
    };

    this.currentChart = echarts.init(container);
    this.currentChart.setOption(option);
  }



  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  public setTheme(theme: 'white' | 'black'): void {
    this.config.theme = toggleWidgetTheme(this.shadowRoot, this.config.theme!) as any;
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderversePricePerformance_old: typeof TraderversePricePerformance_old; } }
if (typeof window !== 'undefined') { window.TraderversePricePerformance_old = TraderversePricePerformance_old; }
